import {
  ChangeDetectorRef,
  Component,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import {FormsModule, ReactiveFormsModule, UntypedFormGroup} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {
  ColumnConfiguration,
  ComponentFieldModel,
  DropdownFieldModel,
  OptionItem,
  OptionProvider
} from '@twpub/core/models';
import {ControlType} from '@twpub/core/enums';
import {Observable, Subscription} from 'rxjs';
import {NGXLogger} from 'ngx-logger';
import {DictionaryService, FeatureFlagService} from '@twpub/core/services';
import {map} from 'rxjs/operators';
import {ColumnConfigEditorComponent} from '@twpub/shared/components';

import {MatError, MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatTooltip} from '@angular/material/tooltip';
import {MatOption, MatSelect} from '@angular/material/select';
import {ImageEditorComponent} from '../image-editor/image-editor.component';
import {JsonInputComponent} from '../json-input/json-input.component';
import {JsonValidatorDirective} from '@twpub/shared/directives/json-validator.directive';
import {MatButton} from '@angular/material/button';
import {MatSlideToggle} from '@angular/material/slide-toggle';

@Component({
  selector: 'pub-component-editor-field',
  templateUrl: './component-editor-field.component.html',
  styleUrls: ['./component-editor-field.component.scss'],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MatError, MatFormField, MatLabel, MatInput, MatTooltip, MatSelect, MatOption, ImageEditorComponent, JsonInputComponent, JsonValidatorDirective, MatButton, MatSlideToggle]
})
export class ComponentEditorFieldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() fieldModel!: ComponentFieldModel<any>;
  @Input() form!: UntypedFormGroup;

  // eslint-disable-next-line no-undef
  fieldType = ControlType;
  fieldComp?: DropdownFieldModel;
  resolvedOptions: OptionItem[] = [];
  dictionaryIds: number[] = [];
  private optionsSub?: Subscription;
  private valueChangesSub?: Subscription;
  private readySub?: Subscription;
  private featureFlagsReady = false;
  isFieldVisible(): boolean {
    if (!this.fieldComp) {
      return false;
    }
    // If field has a dependencyFlag, check if that feature flag is enabled
    if (this.fieldComp.dependencyFlag) {
      // Only check if flags are ready, otherwise assume visible to prevent flickering
      if (!this.featureFlagsReady) {
        return true;
      }
      return this.featureFlagService.isFeatureEnabled(this.fieldComp.dependencyFlag, false);
    }
    // Otherwise, field is visible by default
    return true;
  }

  constructor(
    private logger: NGXLogger,
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    private dictService: DictionaryService,
    private dialog: MatDialog,
    private featureFlagService: FeatureFlagService
  ) {
  }

  ngOnInit(): void {
    this.fieldComp = this.fieldModel;
    this.resolveOptions();
    this.setupValueChangeListener();
    this.setInitialValue(this.fieldComp.value);
    this.dictService.getDictionaries(true).pipe(
      map((dictionaries: any[]) => (dictionaries || []).map(d => d.id))
    ).subscribe((dictionaries: any[]) => {
      this.dictionaryIds = dictionaries
    });

    this.featureFlagService.waitForReady(() => {
      this.featureFlagsReady = true;
      this.cdr.detectChanges();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fieldModel && !changes.fieldModel.firstChange) {
      this.unsubscribeValueChanges();
      this.fieldComp = this.fieldModel;
      this.resolveOptions();
      this.setupValueChangeListener();
      this.setInitialValue(this.fieldComp.value);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeOptions();
    this.unsubscribeValueChanges();
    if (this.readySub) {
      this.readySub.unsubscribe();
    }
  }

  setValue(value: any) {
    if (this.fieldComp) {
      let finalValue = value;
      if (this.fieldComp.controlType === ControlType.Dropdown && this.fieldComp.multiple && Array.isArray(value)) {
        finalValue = value.filter(v => v).join(';');
        finalValue = value.filter(v => v).join(';');
      } else if (this.fieldComp.controlType === ControlType.Boolean) {
        // For boolean fields, preserve false as false (not empty string)
        finalValue = value === false ? false : (value || false);
      }
      this.form.get(this.fieldComp.key)?.setValue(finalValue, {emitEvent: false});
    }
  }

  private setInitialValue(value: any) {
    if (!this.fieldComp) return;

    const control = this.form.get(this.fieldComp.key);
    if (!control) return;

    if (this.fieldComp.controlType === ControlType.Dropdown && this.fieldComp.multiple) {
      if (Array.isArray(value)) {
        const stringValue = value.filter(v => v).join(';');
        control.setValue(stringValue, {emitEvent: false});
      } else {
        control.setValue(value || '', {emitEvent: false});
      }
    } else if (this.fieldComp.controlType === ControlType.Boolean) {
      control.setValue(value === false ? false : (value || false), {emitEvent: false});
    } else {
      control.setValue(value || '', {emitEvent: false});
    }
  }

  private setupValueChangeListener() {
    if (!this.fieldComp) return;
    const control = this.form.get(this.fieldComp.key);
    if (control && this.fieldComp.controlType === ControlType.Dropdown && this.fieldComp.multiple) {
      this.valueChangesSub = control.valueChanges.subscribe(value => {
        if (Array.isArray(value)) {
          const stringValue = value.filter(v => v).join(';');
          if (control.value !== stringValue) {
            control.setValue(stringValue, {emitEvent: false});
          }
        }
      });
    }
  }

  private unsubscribeValueChanges() {
    if (this.valueChangesSub) {
      this.valueChangesSub.unsubscribe();
      this.valueChangesSub = undefined;
    }
  }

  private unsubscribeOptions() {
    if (this.optionsSub) {
      this.optionsSub.unsubscribe();
      this.optionsSub = undefined;
    }
  }

  private resolveOptions(): void {
    this.unsubscribeOptions();
    this.resolvedOptions = [];
    if (!this.fieldComp || !this.fieldComp.options) {
      return;
    }

    const opts: any = (this.fieldComp as any).options;

    if (Array.isArray(opts)) {
      this.resolvedOptions = opts as OptionItem[];
      return;
    }

    if (typeof opts === 'function') {
      try {
        const obs: Observable<OptionItem[]> = (opts as OptionProvider)(this.injector) as Observable<OptionItem[]>;
        if (!obs || typeof (obs as any).subscribe !== 'function') {
          this.logger.error('Option provider must return an Observable<OptionItem[]>');
          this.resolvedOptions = [];
          return;
        }
        this.optionsSub = obs.subscribe({
          next: (items: OptionItem[]) => {
            this.resolvedOptions = items || [];
            this.cdr.markForCheck();
          },
          error: (err: any) => {
            this.logger.error('Failed to load field options', err);
            this.resolvedOptions = [];
            this.cdr.markForCheck();
          }
        });
      } catch (err) {
        this.logger.error('Error calling option provider', err);
        this.resolvedOptions = [];
      }
    } else {
      this.logger.error('Field options is not a provider function');
      this.resolvedOptions = [];
    }
  }

  // Return options to show in template (resolved from the provider)
  get displayedOptions(): OptionItem[] {
    return this.resolvedOptions;
  }

  getMultiSelectValue(key: string): string[] {
    const control = this.form.get(key);
    if (!control) return [];

    const value = control.value;
    if (typeof value === 'string' && value) {
      return value.split(';').filter(v => v);
    }
    return Array.isArray(value) ? value : [];
  }

  onMultiSelectChange(key: string, selectedValues: string[]) {
    const control = this.form.get(key);
    if (!control) return;

    const stringValue = Array.isArray(selectedValues)
      ? selectedValues.filter(v => v).join(';')
      : '';
    control.setValue(stringValue, {emitEvent: true});
  }

  openColumnConfigDialog() {
    const currentValue = this.form.get(this.fieldComp!.key)?.value;
    let columnConfig: ColumnConfiguration | undefined;
    if (typeof currentValue === 'string' && currentValue.trim()) {
      try {
        columnConfig = JSON.parse(currentValue);
      } catch (e) {
        this.logger.error('Failed to parse column config JSON', e);
        columnConfig = undefined;
      }
    } else if (currentValue && typeof currentValue === 'object') {
      columnConfig = currentValue;
    }

    const dialogRef = this.dialog.open(ColumnConfigEditorComponent, {
      data: {
        dictionaryIds: this.dictionaryIds,
        columnConfig
      },
      width: '80%'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.setValue(JSON.stringify(result));
      }
    });
  }
}
